import type { EnsembleSensitivities, Sensitivity } from "@framework/EnsembleSensitivities";
import { SensitivityType } from "@framework/EnsembleSensitivities";

import { SensitivitySortBy, type EnsemblePerRealizationResponse, type SensitivityResponse } from "./types";

// Extract response values for the relevant realizations, in the order of `realizations`
export function extractResponseValues(
    ensemblePerRealResponse: EnsemblePerRealizationResponse,
    realizations: number[],
): number[] {
    const valueByRealization = new Map(
        ensemblePerRealResponse.realizations.map((real, idx) => [real, ensemblePerRealResponse.values[idx]]),
    );
    return realizations.flatMap((real) => {
        const value = valueByRealization.get(real);
        return value !== undefined ? [value] : [];
    });
}

export function computeAverage(values: number[]): number {
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
}

/** False for the empty side of a single-case scenario, which sits at the reference. */
export function hasCaseOnSide(response: SensitivityResponse, side: "low" | "high"): boolean {
    return side === "low"
        ? response.lowCaseName !== "" || response.lowCaseRealizations.length > 0
        : response.highCaseName !== "" || response.highCaseRealizations.length > 0;
}

// Sensitivity realizations extraction
export function extractSensitivityRealizations(sensitivity: Sensitivity): number[] {
    return sensitivity.cases.flatMap((c) => c.realizations);
}

// Sorting functions
export const sortSensitivityResponses = (
    responses: SensitivityResponse[],
    referenceSensitivity: string,
    sortBy: SensitivitySortBy,
): SensitivityResponse[] => {
    if (sortBy === SensitivitySortBy.ALPHABETICAL) {
        return [...responses].sort((a, b) => b.sensitivityName.localeCompare(a.sensitivityName)); // Reverse alphabetical
    }

    return [...responses].sort((a, b) => {
        // Sort reference sensitivity last
        if (a.sensitivityName === referenceSensitivity) return 1;
        if (b.sensitivityName === referenceSensitivity) return -1;

        const maxA = Math.max(Math.abs(a.lowCaseReferenceDifference), Math.abs(a.highCaseReferenceDifference));
        const maxB = Math.max(Math.abs(b.lowCaseReferenceDifference), Math.abs(b.highCaseReferenceDifference));
        return maxA - maxB;
    });
};
export function computeReferenceAverage(
    sensitivities: EnsembleSensitivities,
    ensemblePerRealResponse: EnsemblePerRealizationResponse,
    referenceSensitivity: string,
): number {
    const refSensitivity = sensitivities.getSensitivityByName(referenceSensitivity);
    const refRealizations = extractSensitivityRealizations(refSensitivity);
    const refValues = extractResponseValues(ensemblePerRealResponse, refRealizations);
    return computeAverage(refValues);
}
// Relative tolerance for "no impact": volumes summed in a different order differ in the last bits.
const NO_IMPACT_RELATIVE_TOLERANCE = 1e-6;

function isNegligibleDifference(difference: number, referenceAverage: number): boolean {
    return Math.abs(difference) <= Math.abs(referenceAverage) * NO_IMPACT_RELATIVE_TOLERANCE;
}

function hasNoImpact(
    response: SensitivityResponse,
    referenceResponse: SensitivityResponse | undefined,
    referenceAverage: number,
): boolean {
    if (response.sensitivityType === SensitivityType.MONTECARLO) {
        // P10/P90 always differ from the reference mean by the seed spread, so compare with the reference's own stats.
        if (!referenceResponse || referenceResponse.sensitivityType !== SensitivityType.MONTECARLO) {
            return false;
        }
        return (
            isNegligibleDifference((response.sensitivityAverage ?? 0) - referenceAverage, referenceAverage) &&
            isNegligibleDifference(response.lowCaseAverage - referenceResponse.lowCaseAverage, referenceAverage) &&
            isNegligibleDifference(response.highCaseAverage - referenceResponse.highCaseAverage, referenceAverage)
        );
    }
    return (
        isNegligibleDifference(response.lowCaseReferenceDifference, referenceAverage) &&
        isNegligibleDifference(response.highCaseReferenceDifference, referenceAverage)
    );
}

export function filterSensitivityResponses(
    responses: SensitivityResponse[],
    hideNoImpactSensitivities: boolean,
    referenceSensitivity: string,
    referenceAverage: number,
): SensitivityResponse[] {
    if (!hideNoImpactSensitivities) return responses;

    const referenceResponse = responses.find((response) => response.sensitivityName === referenceSensitivity);
    return responses.filter(
        (response) =>
            response.sensitivityName === referenceSensitivity ||
            !hasNoImpact(response, referenceResponse, referenceAverage),
    );
}
/** Default reference sensitivity: `rms_seed`, else `rms`, else the first available sensitivity. */
export function pickDefaultReferenceSensitivityName(sensitivityNames: readonly string[]): string | null {
    if (sensitivityNames.includes("rms_seed")) {
        return "rms_seed";
    }
    if (sensitivityNames.includes("rms")) {
        return "rms";
    }
    return sensitivityNames[0] ?? null;
}

export function getReferenceSensitivityName(
    sensitivities: EnsembleSensitivities,
    referenceSensitivity: string,
): string {
    if (!referenceSensitivity || !sensitivities.hasSensitivityName(referenceSensitivity)) {
        const defaultName = pickDefaultReferenceSensitivityName(sensitivities.getSensitivityNames());
        if (!defaultName) {
            throw new Error("No sensitivities available");
        }
        return defaultName;
    }
    return referenceSensitivity;
}
